import { useMemo } from "react";

import {
  selectMaritimeWarnings,
  useMaritimeAreasQuery,
  useMaritimeWarningsQuery,
} from "@/features/dashboard/components/warnings/maritime-warnings.queries";
import { highestMaritimeWarningLevelForEnabledDomains } from "@/features/dashboard/components/warnings/maritime-warnings.utils";
import { WarningColor } from "@/features/shared/hooks/useHighestWarning";
import { useDashboardWarningSettings } from "@/features/shared/hooks";

import { toolbarWarningButtonColor } from "./toolbar-warning-color";

/**
 * Maritime toolbar button colour: highest maritime warning in the enabled domains.
 * Undefined (button hidden) without a warning, and while loading or on error so the
 * button never claims "no warning" without data. Land warnings have their own button.
 */
export function useMaritimeWarningColor(): WarningColor | undefined {
  const { enabledMaritimeDomains } = useDashboardWarningSettings();
  const enabled = enabledMaritimeDomains.length > 0;

  const maritimeWarningsQuery = useMaritimeWarningsQuery({ enabled });
  const maritimeAreasQuery = useMaritimeAreasQuery({ enabled });
  const hasData = maritimeWarningsQuery.isSuccess && maritimeAreasQuery.isSuccess;

  return useMemo(
    () =>
      hasData
        ? toolbarWarningButtonColor(
            highestMaritimeWarningLevelForEnabledDomains(
              selectMaritimeWarnings(maritimeWarningsQuery.data),
              maritimeAreasQuery.data ?? [],
              enabledMaritimeDomains,
            ),
          )
        : undefined,
    [hasData, maritimeWarningsQuery.data, maritimeAreasQuery.data, enabledMaritimeDomains],
  );
}
